/**
 * The tenant area's typed edge.
 *
 * One Zod schema per tenant-facing response body. Every route here is a
 * declared contract, not code that exists yet — so these schemas ARE the
 * contract: `fixtures.ts` and `handlers.ts` are built against them, and every
 * request goes through `parseOrThrow` (`src/lib/http/parse.ts`) with one of
 * these before it renders. A field renamed on the real API surfaces here as a
 * parse failure, not a blank cell.
 */

import { z } from 'zod';

export const creditsSchema = z.object({
  balance: z.number().int(), // may be NEGATIVE after an overrun
  held: z.number().int(),
  available: z.number().int(),
  // The current UTC calendar month off the ledger; `month_spent` is a positive figure.
  month_opening: z.number().int().optional(),
  month_added: z.number().int().optional(),
  month_spent: z.number().int().optional(),
  unit: z.literal('tenant_credits'),
});
export type Credits = z.infer<typeof creditsSchema>;

export const membershipRowSchema = z.object({
  id: z.string(),
  user_id: z.string(),
  // Null for a member whose address is not verified yet (`/control/me`
  // stores NULL) — the row still renders; it is never filtered out.
  email: z.string().nullable(),
  role: z.enum(['TENANT_ADMIN', 'CAMPAIGN_ANALYST', 'ADDRESS_READER']),
  campaign_id: z.string().nullable(),
  campaign_name: z.string().nullable(),
});
export type MembershipRow = z.infer<typeof membershipRowSchema>;

export const invitationSchema = z.object({
  id: z.string(),
  email: z.string(),
  role: z.enum(['TENANT_ADMIN', 'CAMPAIGN_ANALYST']), // ADDRESS_READER is unrepresentable
  campaign_id: z.string().nullable(),
  campaign_name: z.string().nullable(),
  created_at: z.string(),
  expires_at: z.string(),
  accepted_at: z.string().nullable(),
  revoked_at: z.string().nullable(),
});
export type Invitation = z.infer<typeof invitationSchema>;

/** The 201 body of `POST /tenants/{t}/invitations`, and the only place the
 *  invitation's link is ever returned — the list row above holds a keyed
 *  digest, and no route re-fetches a token (§18.2). Kept as its own schema
 *  rather than widening `invitationSchema`, since a list read that somehow
 *  carried a live link would be a credential leak. */
export const issuedInvitationSchema = z.object({
  id: z.string(),
  email: z.string(),
  link: z.string(),
  expires_at: z.string(),
});
export type IssuedInvitation = z.infer<typeof issuedInvitationSchema>;

/** §18.5's funded window, and what falls outside it. The route does not
 *  exist today.
 *
 *  Every figure here is the server's; the client derives none of them: the
 *  window depends on §6.6's arithmetic, on §6.5's shed order, and on which
 *  operations have a price row at all (§18.1), and a client that recomputed
 *  it would be a second implementation of a calculation that must have
 *  exactly one. */
export const accountRefSchema = z.object({
  id: z.string(),
  handle: z.string(),
  platform: z.enum(['instagram', 'tiktok']),
  /** Which campaign's tracking put this account in the set. Under A18 one target
   *  is shared by every campaign tracking the account and shedding is per
   *  subscriber (§6.5), so an account can be funded for one campaign and shed
   *  for another — and without this field a shed row cannot be linked to the
   *  editor that would change it. */
  campaign_id: z.string(),
  credits_per_hour: z.number().nullable(), // null when an operation it needs is unpriced
});
export type AccountRef = z.infer<typeof accountRefSchema>;

export const campaignCoverageSchema = z.object({
  campaign_id: z.string(),
  campaign_name: z.string(),
  slots_planned: z.number().int(),
  slots_unfunded: z.number().int(),
  first_unfunded_at: z.string().nullable(), // ISO-8601; null when nothing is unfunded
});
export type CampaignCoverage = z.infer<typeof campaignCoverageSchema>;

export const fundedWindowSchema = z.object({
  unit: z.literal('tenant_credits'),
  credits_per_hour: z.number().nullable(), // priced burn rate of the tracked set
  funded_until: z.string().nullable(), // ISO-8601; null when the window is zero-length
  horizon_until: z.string(), // end of the materialised horizon
  /** What would fund every planned slot to `horizon_until` at the current rate.
   *  Asked of the server rather than derived, for the reason above. Null when
   *  the rate is unknown because some operation is unpriced. */
  credits_to_fund_horizon: z.number().int().nullable(),
  campaigns: z.array(campaignCoverageSchema),
  funded_accounts: z.array(accountRefSchema),
  /** §18.5's shed set, in §6.5's shed order. Empty at a zero balance, where
   *  nothing is funded and therefore nothing was shed to make room. */
  shed_accounts: z.array(accountRefSchema),
});
export type FundedWindow = z.infer<typeof fundedWindowSchema>;

export const tenantSchema = z.object({ id: z.string(), name: z.string() });
export type Tenant = z.infer<typeof tenantSchema>;

export const campaignSchema = z.object({
  id: z.string(),
  name: z.string(),
  timezone: z.string(),
  active_from: z.string().nullable(),
  active_to: z.string().nullable(),
});
export type Campaign = z.infer<typeof campaignSchema>;

/** One calendar day, in the campaign's own timezone. `views` is null for a day
 *  nothing was observed, never a measured zero. */
export const campaignDaySchema = z.object({
  day: z.string(),
  posts: z.number(),
  views: z.number().nullable(),
});
export type CampaignDay = z.infer<typeof campaignDaySchema>;

/** A row of `GET /tenants/{t}/campaigns`: the campaign plus its last seven days.
 *  `reach_7d`/`posts_7d` are null and `daily` empty for a campaign that tracks
 *  no account, so no reading is possible. */
export const campaignSummarySchema = campaignSchema.extend({
  state: z.enum(['ACTIVE', 'PAUSED', 'ARCHIVED']),
  reach_7d: z.number().nullable(),
  posts_7d: z.number().nullable(),
  daily: z.array(campaignDaySchema),
});
export type CampaignSummary = z.infer<typeof campaignSummarySchema>;

export const itemsOf = <T extends z.ZodTypeAny>(item: T) => z.object({ items: z.array(item) });

// -- List envelopes for the routes in the table that return more than one row.
export const tenantsListSchema = itemsOf(tenantSchema);
export const membersListSchema = itemsOf(membershipRowSchema);
export const invitationsListSchema = itemsOf(invitationSchema);

/** Roles a person may be INVITED to. Not the roles that exist (§5.1). */
export const INVITABLE_ROLES = ['TENANT_ADMIN', 'CAMPAIGN_ANALYST'] as const;

export const dataRetentionSchema = z.object({
  campaign_seconds: z.number().int(),
  unrelated_media_seconds: z.number().int(),
});
